import angular from "angular";

import { SitemapNode, SitemapVisibility, Configs } from "@salaxy/core";
import { NaviService, SessionService, UserSettingsService } from "../../services";
import { SitemapHelper } from "../../helpers";
import { CurrentRoute } from "../../helpers/CurrentRoute";

/**
 * Helper controller to generate navigation components and views:
 * Top- and side-menus, paths and controls that show the current title.
 * These controls take the navigation logic from an object (sitemap) and are aware of current node / open page on that sitemap.
 *
 * NOTE: This is just an optional helper to make creating simple demo sites easier.
 * There is no need to use NaviService, NaviController or components in your custom site!
 * You can use something completely different.
 */
export class NaviController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["NaviService", "SessionService", "UserSettingsService"];

  /**
   * Display mode of the sitemap / navi: "default", "accordion" or "horizontal"
   * default: Shows full two levels of the tree. Typically used as sitemap in the content area.
   * accordion: Shows first level of the navi tree and second level only it is in the current path. Typically used in left menu navigation.
   * horizontal: Full navi on top bar. Second tier in dropdowns.
   * 3-levels: Accordion with 3 levels.
   */
  public mode: "default" | "accordion" | "horizontal" | "3-levels" = "default";

  /**
   * - layout: "default" or "iframe"
   * - default: Basic layout with full navigation (left sidebar or horizontal).
   * - iframe: Layout without first tier navigation on left sidebar. Second tier navigation items are under dropdown in top bar / header if mode is default.
   * On horizontal mode full navigation is on top bar.
   */
  public layout: "default" | "iframe";

  /**
   * Creates a new NaviController
   * @param naviService - Service that stores the navigation state.
   * @ignore
   */
  constructor(private naviService: NaviService, private sessionService: SessionService, private userSettingsService: UserSettingsService) { }

  /**
   * Implement IController
   */
  public $onInit = () => {

    if (this.mode !== "accordion" && this.mode !== "3-levels" && this.mode !== "horizontal") {
      this.mode = "default";
    }

  };

  /**
   * Contains information about the current sitemap node (if resoved) and other data related to route.
   * This property takes into account that the current node may not be found,
   * i.e. current.node may be null, current.path may be [].
   * However, the "route" as an object should never be null.
   * All the other properties in this controller use first node as default for historical reasons.
   */
  public get route(): CurrentRoute {
    return this.naviService.current;
  }

  /**
   * Returns the main sitemap object that is used in rendering the navigation.
   * Set the sitemap by setting the angular constant or object "SITEMAP"
   */
  public get sitemap(): SitemapNode[] {
    return this.naviService.getSitemap();
  }

  /**
   * If true, the layout should show a full-page loader instead of normal pages.
   * Controllers in normal pages should be destoyed (e.g. using ng-if) so that this can also
   * be used in reloading the AngularJS user interface without full Browser reload.
   */
  public get showFullPageLoader() {
    return this.sessionService.isLoading;
  }

  /**
   * Gets the user-specific setting (service), stored in the local storage.
   */
  public get userSettings() {
    return this.userSettingsService;
  }

  /**
   * Temporary visibility helper for the new Payroll 4 version.
   */
  public get newPayrollVersion() {
    const show = true; //always show the new version switch for now
    const result = {
      /** If true, the switch for selecting version 4 is shown to the user. */
      show,
      /** If true the new version is in use. */
      use: show && (this.userSettings.ng1Binder("payroll.newVersion").val ?? true) as boolean,
    };
    if (this.sessionService.session.settings.integration?.serverUrl || this.sessionService.isInRole("aidemo")) {
      // Integration server requires the new version
      result.show = false;
      result.use = true;
    }
    return result;
  }


  /** New payroll version use */
  public get newPayrollVersionUse() {
    return this.newPayrollVersion.use;
  }
  /**
   * New payroll version use
   */
  public set newPayrollVersionUse(value: boolean) {
    this.userSettings.ng1Binder("payroll.newVersion").val = value ?? true;
  }

  /**
   * Returns route for the given path
   * @param path Page URL path for the node.
   * @returns Route for the path.
   */
  public getRoute(path: string = null): CurrentRoute {
    if (!path) {
      return this.route;
    }
    return new CurrentRoute(this.naviService.getNodesByPath(path));
  }

  /**
   * Retuns the identifier of the currently selected section.
   * If nothing else is selected, it will return the first section, by convention called 'home'.
   */
  public getSectionId() {
    if (!this.naviService.currentSection) {
      return "home";
    }
    return this.naviService.currentSection.id;
  }

  /**
   * Gets the navi URL from a sitemap node.
   * Currently, removes "*" at the end if url ends with "/*", but may be used for additional logic in the future.
   */
  public getUrl(node: SitemapNode): string {
    if (!node || !node.url) {
      return null;
    }
    if (node.url.endsWith("/*")) {
      return node.url.substr(0, node.url.length - 1);
    }
    return node.url;
  }

  /** Toggles the isNaviOpen property */
  public toggleNavi() {
    this.naviService.isNaviOpen = !this.naviService.isNaviOpen;
  }

  /**
   * True if the navigation (e.g. left sidebar) is open.
   * Typically, this property is used only in narrow (mobile) view, otherwise the sidebar is always shown.
   */
  public get isNaviOpen() { return this.naviService.isNaviOpen; }
  public set isNaviOpen(value: boolean) { this.naviService.isNaviOpen = value; }

  /** Toggles the isSidebarOpen property */
  public toggleSidebar() {
    this.naviService.isSidebarOpen = !this.naviService.isSidebarOpen;
  }

  /**
   * True if the secondary sidebar (e.g. right sidebar) is open.
   */
  public get isSidebarOpen() { return this.naviService.isSidebarOpen; }
  public set isSidebarOpen(value: boolean) { this.naviService.isSidebarOpen = value; }

  /**
   * Gets the current section: The first level node in the sitemap
   * @returns Section filtered by the roles.
   */
  public getSection(): SitemapNode {
    return this.naviService.getCurrentSection();
  }

  /**
   * Returns true if the node should be hidden in the navi.
   * @param node Node to check. If null, the current node is checked (not defaulted to first node).
   */
  public getHidden(node: SitemapNode = null): boolean {
    return SitemapHelper.getVisibility(node || this.route.node, SitemapVisibility.Hidden);
  }

  /**
   * Gets the visibility:disabled flag for the current node OR any node in the path
   */
  public getDisabled(): boolean {
    return this.route.node?.visibility == SitemapVisibility.Disabled || !!(this.route.path).find((x) => x.visibility == SitemapVisibility.Disabled);
  }

  /**
   * Returns true if the node has non-hidden children.
   * @param node Node to check
   */
  public hasChildren(node: SitemapNode): boolean {
    return !!node?.children.find((x) => !SitemapHelper.getVisibility(x, SitemapVisibility.Hidden));
  }

  /**
   * Filters away the hidden nodes from node array.
   * @param nodes Node array.
   */
  public filterHidden(nodes: SitemapNode[]): SitemapNode[] {
    if (!nodes) {
      return [];
    }
    return nodes.filter((x) => !SitemapHelper.getVisibility(x, SitemapVisibility.Hidden));
  }

  /**
   * Returns true if the given node is the currently selected node.
   */
  public isCurrent(siteMapNode: SitemapNode): boolean {
    return this.naviService.isCurrent(siteMapNode);
  }

  /**
   * Returns true if the given node is the currently selected node.
   */
  public isCurrentSection(section: SitemapNode): boolean {
    return this.naviService.isCurrentSection(section);
  }

  /**
   * In test environment, returns true if the sitemap node has role "test" and it is visible only because of that role.
   * @param siteMapNode Node in the sitemap.
   */
  public isTest(siteMapNode: SitemapNode): boolean {
    return siteMapNode.visibleTestOnly >= 0;
  }

  /**
   * Navigate to given path
   * @param path - Path to navigate.
   * @param checkSession - If true, triggers session refresh
   */
  public navigateTo(path: string, checkSession = false) {
    this.naviService.navigateTo(path, checkSession);
  }

  /** Returns the page / node title */
  public get title() {
    return this.naviService.title || this.naviService.sectionTitle || "\xa0";
  }

  /** Gets the currently selected node. */
  public get current() {
    return this.naviService.currentNode;
  }

  /** Current path from root to the current node. */
  public get currentPath(): SitemapNode[] {
    return this.naviService.currentPath;
  }

  /** Returns true if the current window is in an IFrame */
  public get isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  /** Returns current year. Used for example in copyright texts in footers */
  public getCurrentYear(): number {
    return new Date().getFullYear();
  }

  /**
   * Determines whether to show the KYC (Know Your Customer) information.
   * @returns A boolean value indicating whether to show the KYC information.
   */
  public get showKyc(): boolean {
    return this.sessionService.kycState == "missing" || this.sessionService.kycState == "expired";
  }

  /** Returns current application */
  public get applicationId(): string {
    return Configs.current.applicationId;
  }
}
